import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import ResourceSummary from "../components/ResourceSummary";

const RUBRIC_COLOR = {
  Strong:     { fg: "#22645A", chip: "#F0F7F4" },
  Developing: { fg: "#7A5D2F", chip: "#FAF5E8" },
  Emerging:   { fg: "#7A2629", chip: "#FBEDED" },
  "At Risk":  { fg: "#5A1B1E", chip: "#F4DCDE" },
};

/**
 * The respondent reads this. Talks to *you* throughout.
 * Token = the same one they used for the survey; permanent.
 * The deployment must have `individual_reports_enabled: true`.
 */
export default function RespondentReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: payload } = await api.get(`/api/respondent-report/${token}/data`);
        setData(payload);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <Loading />;
  if (error) return <ErrorScreen message={error} />;
  if (!data) return null;

  const date = data.completed_at
    ? new Date(data.completed_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-paper" data-testid="respondent-report">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">

        <header className="mb-12">
          <div className="eyebrow mb-3" style={{ color: "#2F6B7C" }}>
            A reflection prepared for you
          </div>
          <h1
            className="mb-4"
            style={{
              fontSize: "clamp(2.25rem, 4.5vw, 3.25rem)",
              fontFamily: "Oswald",
              color: "#AD393E",
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.005em",
            }}
            data-testid="report-title"
          >
            Your reading on the seven dimensions.
          </h1>
          <p className="text-sm text-gray-600 max-w-prose" data-testid="report-meta">
            {data.organization_name} &middot; {data.deployment_name} &middot; reflected {date}
          </p>
        </header>

        <section className="mb-16">
          <p className="lead text-lg md:text-xl text-gray-800 max-w-prose mb-2" style={{ fontFamily: "Source Serif 4, Georgia, serif", fontStyle: "italic", lineHeight: 1.55 }}>
            What follows is a reading of your own answers. It is not a score on you,
            and it is not how you compare to anyone else.
          </p>
          <p className="text-sm text-gray-600 max-w-prose">
            Each dimension carries a single short paragraph. Read what lands; leave
            what doesn&rsquo;t.
          </p>
        </section>

        <section className="mb-20" data-testid="dimensions-section">
          <DimensionsAtAGlance dimensions={data.dimensions} bands={data.rubric_band_thresholds_pct} />
        </section>

        <section className="mb-20 space-y-12" data-testid="dimension-readings">
          {data.dimensions.map((d) => (
            <DimensionReading key={d.dimension} d={d} />
          ))}
        </section>

        {data.resources && (
          <div className="mb-20">
            <ResourceSummary resources={data.resources} meta={data.resources_meta} register="individual" />
          </div>
        )}

        {data.reflections.length > 0 && (
          <section className="mb-20" data-testid="reflections-section">
            <div className="eyebrow mb-3" style={{ color: "#AD393E" }}>You wrote</div>
            <h2 className="text-2xl mb-6" style={{ fontFamily: "Oswald", color: "#1A1A1A", fontWeight: 600 }}>
              The words you used about your own work.
            </h2>
            <p className="text-sm text-gray-700 max-w-prose mb-8">
              These are the answers you typed during the survey, kept exactly as you
              wrote them. Nothing here was scored or summarized — your words stay yours.
            </p>
            <ul className="space-y-8">
              {data.reflections.map((r) => (
                <ReflectionRow key={r.number} r={r} />
              ))}
            </ul>
          </section>
        )}

        <footer className="border-t border-gray-200 pt-8 mt-16" data-testid="report-disclaimer">
          <p className="text-xs text-gray-500 max-w-prose leading-relaxed">{data.disclaimer}</p>
        </footer>
      </div>
    </div>
  );
}

/** A horizontal-track visualization replacing the radar chart.
 * Each dimension is one row: 1.0–5.0 scale, with the rubric bands shaded
 * behind a single marker dot at the respondent's mean. Reads as a
 * measurement instrument, not a marketing infographic. */
function DimensionsAtAGlance({ dimensions, bands }) {
  return (
    <div data-testid="dimensions-tracks">
      <div className="eyebrow mb-3" style={{ color: "#2F6B7C" }}>The seven readings</div>
      <h2 className="text-2xl mb-6" style={{ fontFamily: "Oswald", color: "#1A1A1A", fontWeight: 600 }}>
        Where each dimension sits, on a 1–5 scale.
      </h2>
      <div className="space-y-5">
        {dimensions.map((d) => (
          <DimensionTrack key={d.dimension} d={d} bands={bands} />
        ))}
      </div>
      <div className="flex items-center gap-5 mt-8 text-xs" style={{ fontFamily: "Oswald", letterSpacing: "0.12em", color: "#666" }}>
        <LegendChip label="At Risk" color="#F4DCDE" />
        <LegendChip label="Emerging" color="#FBEDED" />
        <LegendChip label="Developing" color="#FAF5E8" />
        <LegendChip label="Strong" color="#F0F7F4" />
      </div>
    </div>
  );
}

function DimensionTrack({ d, bands }) {
  // bands maps level -> scale_pct (left edge of that band on the 0..100 axis).
  // Order from low to high: 0 → at-risk → emerging → developing → strong → 100.
  const stops = [
    { from: 0, to: bands.Emerging, level: "At Risk" },
    { from: bands.Emerging, to: bands.Developing, level: "Emerging" },
    { from: bands.Developing, to: bands.Strong, level: "Developing" },
    { from: bands.Strong, to: 100, level: "Strong" },
  ];
  const c = RUBRIC_COLOR[d.rubric_level] || RUBRIC_COLOR.Developing;
  return (
    <div data-testid={`track-${d.dimension}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
          {d.label}
        </div>
        <div className="text-xs" style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: c.fg }}>
          {d.mean.toFixed(2)} &middot; {d.rubric_level}
        </div>
      </div>
      <div className="relative h-3" style={{ background: "#F5F2EA" }}>
        {stops.map((s, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${s.from}%`,
              width: `${s.to - s.from}%`,
              top: 0,
              bottom: 0,
              background: RUBRIC_COLOR[s.level]?.chip || "#F5F2EA",
              borderRight: i < stops.length - 1 ? "1px solid #FBFAF7" : "none",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            left: `calc(${d.scale_pct}% - 6px)`,
            top: "-3px",
            width: "12px",
            height: "18px",
            background: "#1A1A1A",
            border: "2px solid #FBFAF7",
          }}
          data-testid={`track-${d.dimension}-marker`}
          aria-label={`${d.label} score: ${d.mean.toFixed(2)}`}
          title={`${d.mean.toFixed(2)} — ${d.rubric_level}`}
        />
      </div>
      {/* 1..5 axis ticks */}
      <div className="flex justify-between mt-1 text-[10px] text-gray-400" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
        <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
      </div>
    </div>
  );
}

function LegendChip({ label, color }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ display: "inline-block", width: "10px", height: "10px", background: color, border: "1px solid rgba(0,0,0,0.05)" }} />
      <span>{label}</span>
    </span>
  );
}

function DimensionReading({ d }) {
  const c = RUBRIC_COLOR[d.rubric_level] || RUBRIC_COLOR.Developing;
  return (
    <div data-testid={`reading-${d.dimension}`}>
      <div className="flex items-baseline gap-3 mb-2 flex-wrap">
        <h3 className="text-xl" style={{ fontFamily: "Oswald", color: "#1A1A1A", fontWeight: 600 }}>
          {d.label}
        </h3>
        <span
          className="text-xs px-2 py-0.5"
          style={{
            background: c.chip,
            color: c.fg,
            fontFamily: "Oswald",
            letterSpacing: "0.05em",
          }}
        >
          {d.rubric_level}
        </span>
        <span className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
          {d.mean.toFixed(2)} on a 1–5 scale
        </span>
      </div>
      <p className="text-base text-gray-800 max-w-prose" style={{ fontFamily: "Source Serif 4, Georgia, serif", lineHeight: 1.6 }}>
        {d.personal_copy}
      </p>
    </div>
  );
}

function ReflectionRow({ r }) {
  return (
    <li data-testid={`reflection-${r.number}`}>
      <div className="text-xs text-gray-500 mb-2" style={{ fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase" }}>
        Tier {r.tier} &middot; {r.code}
      </div>
      <p className="text-sm text-gray-700 mb-2" style={{ fontFamily: "Inter" }}>
        {r.prompt}
      </p>
      <p
        className="text-base text-gray-900 max-w-prose"
        style={{
          fontFamily: "Source Serif 4, Georgia, serif",
          fontStyle: "italic",
          lineHeight: 1.55,
          borderLeft: "3px solid #4592A8",
          paddingLeft: "14px",
        }}
      >
        &ldquo;{r.answer}&rdquo;
      </p>
    </li>
  );
}

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="eyebrow text-gray-500">Loading your reflection…</div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-8" data-testid="respondent-report-error">
      <div className="max-w-md text-center">
        <div className="eyebrow mb-6" style={{ color: "#AD393E" }}>
          This link can&rsquo;t be opened
        </div>
        <p className="lead text-lg mb-2">{message || "The link may have expired or be unavailable."}</p>
        <p className="text-sm text-gray-500 mt-6">
          If you believe this is a mistake, reach out to your facilitator.
        </p>
      </div>
    </div>
  );
}
